import { ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router'
import { GoogleOAuthProvider } from '@react-oauth/google'
import * as Sentry from '@sentry/react'
import SidebarMenu from './SidebarMenu/SidebarMenu'
import { StyledSidebarMenu } from './SidebarMenu/styled-components'
import { config } from '@/containers/Login/config'
import useImportScript from '@/shared/hooks/useImportScript'
import Login from '@/containers/Login'
import ErrorPage from '@/containers/ErrorPage'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import i18n from '@/i18n'
import { capitalize } from '@/shared/tools/stringFunctions'
import { useNotificationReceivedListener } from './hooks/useNotificationReceivedListener'
import GamificationNotification from '@/shared/components/GamificationNotification/GamificationNotification'

interface AuthData {
  authTokenSession: string | null
  isAdmin: boolean
  isSelectraAdmin: boolean
  country: string
  userLanguage: string
  applicationType: string
}

interface BackofficePageProps {
  children: ReactNode
}

const BackofficePage = ({ children }: BackofficePageProps) => {
  const {
    authTokenSession,
    isAdmin,
    isSelectraAdmin,
    country,
    userLanguage,
    applicationType: applicationTypeAuth,
  } = useSelector(selectAuthData) as AuthData
  const { gamificationNotification, clearGamificationNotification } = useNotificationReceivedListener()

  const { loadScript } = useImportScript()
  const locationHook = useLocation()
  const [loggedIn, setLoggedIn] = useState(!!authTokenSession)
  const [applicationType, setApplicationType] = useState(applicationTypeAuth)
  const authorizedApplicationTypes = [
    'telecom',
    'energie',
    'users-mngt',
    'ip-addresses-management',
    'assurance',
    'contact',
    'callbacks',
    'dev-tools-v2',
    'additional-sales',
  ]
  const allowedCountries = ['fr', 'be', 'it', 'nl', 'de']
  const hasErrorType = !authorizedApplicationTypes.includes(applicationType)
  const hasErrorCountry = !allowedCountries.includes(country)
  let wrapperClass = 'wrapper'

  if (location.hostname.includes('localhost') || location.hostname.includes('staging')) {
    wrapperClass = 'wrapper-warning'
  }

  useEffect(() => {
    if (!userLanguage || !country) return
    i18n.changeLanguage(`${country}${capitalize(userLanguage)}`)
  }, [userLanguage, country])

  useEffect(() => {
    if (authTokenSession) {
      setLoggedIn(true)
    } else {
      setLoggedIn(false)
    }
  }, [authTokenSession])

  useEffect(() => {
    const splitedPath = locationHook.pathname.split('/')
    const currentType = splitedPath[1] ?? ''

    if (applicationType !== currentType) {
      setApplicationType(currentType)
    }
  }, [locationHook.pathname])

  loadScript('//embed.typeform.com/next/embed.js', false, loggedIn)

  if (!loggedIn) {
    return (
      <GoogleOAuthProvider
        clientId={config.clientId}
        onScriptLoadError={() => {
          if (import.meta.env.PROD) {
            Sentry.captureException(new Error('Google OAuth script failed to load'))
          }
        }}
      >
        <Login />
      </GoogleOAuthProvider>
    )
  }

  if (hasErrorType || hasErrorCountry) {
    return (
      <div className={wrapperClass}>
        <aside id='sidebar-menu'>
          <StyledSidebarMenu>
            <SidebarMenu
              applicationType={applicationType}
              isAdmin={isAdmin ?? false}
              isSelectraAdmin={isSelectraAdmin ?? false}
              error={true}
            />
          </StyledSidebarMenu>
        </aside>
        <ErrorPage
          hasErrorType={hasErrorType}
          hasErrorCountry={hasErrorCountry}
          currentType={applicationType}
          currentCountry={country}
        />
      </div>
    )
  }

  return (
    <div className={wrapperClass}>
      <aside id='sidebar-menu'>
        <StyledSidebarMenu>
          <SidebarMenu
            applicationType={applicationType}
            isAdmin={isAdmin ?? false}
            isSelectraAdmin={isSelectraAdmin ?? false}
            error={false}
          />
        </StyledSidebarMenu>
      </aside>

      <main id='main'>
        <section>{children}</section>
      </main>
      <GamificationNotification notification={gamificationNotification} onClose={clearGamificationNotification} />
      <div
        data-tf-chat
        data-tf-tooltip='HOP assistance'
        data-tf-popover='TcAeeiM1'
        data-tf-button-color='#0445AF'
      ></div>
    </div>
  )
}

export default BackofficePage
